import * as yup from 'yup';
import { t } from '@/localization/i18n';

const REQUIRED_VALIDATION_NAME = 'required';
const REQUIRED_MULTI_SELECT_VALIDATION_NAME = 'CUSTOM_REQUIRED_MULTI_SELECT_VALIDATION';

export const REQUIRED_TEST_NAMES = [
  REQUIRED_VALIDATION_NAME,
  REQUIRED_MULTI_SELECT_VALIDATION_NAME,
];

export const exactLengthValidator = (length: number) =>
  yup
    .string()
    .test(
      'EXACT_LENGTH_VALIDATOR_CUSTOM',
      t('validation.exact_length', { length }),
      (val) => val?.length === length,
    );

export const numericStringExactLength = (
  length: number,
  invalidMessage: string,
  requiredMessage?: string,
) => yup.string().matches(new RegExp(`^\\d{${length}}$`), invalidMessage);

// TODO: remove parameter
export const requiredStringValidation = () =>
  yup.string().required(t('common.form.required_field'));

// TODO: remove parameter
export const requiredMultiSelectValidation = () =>
  yup
    .mixed()
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        return originalValue.split(',').map((item) => item.trim());
      }
      return originalValue || [];
    })
    .test(
      REQUIRED_MULTI_SELECT_VALIDATION_NAME,
      t('common.form.required_field'),
      (value) => Array.isArray(value) && value.length > 0,
    );

// TODO: remove parameter
export const phoneNumberValidator = () =>
  yup.string().matches(/^\d{8,9}$/, t('validation.phoneNumberInvalid'));

export const minLengthValidator = (length: number) =>
  yup
    .string()
    .test(
      'MIN_LENGTH_VALIDATOR',
      t('validation.min_length', { length }),
      (val) => val?.length >= length,
    );

export const maxLengthValidator = (length: number) =>
  yup
    .string()
    .test(
      'MAX_LENGTH_VALIDATOR',
      t('validation.max_length', { length }),
      (val) => val?.length <= length,
    );

export const numberLessThanValidator = (value: number) =>
  yup
    .string()
    .test(
      'NUMBER_LESS_THEN_VALIDATOR',
      t('validation.number_less_than_or_equal', { value }),
      (val) => Number(val) <= value,
    );

export const numberMoreThanValidator = (value: number, allowZero = false) =>
  yup
    .string()
    .test(
      'NUMBER_MORE_THEN_VALIDATOR',
      t('validation.number_more_than_or_equal', { value }),
      (val) => Number(val) >= value || (allowZero && Number(val) === 0),
    );

export const numberBetweenValidator = (min: number, max: number) =>
  yup
    .string()
    .test(
      'NUMBER_BETWEEN_VALIDATOR',
      t('validation.number_between', { min, max }),
      (val) => !Number(val) || (Number(val) >= min && Number(val) <= max),
    );

// Unlike numberBetweenValidator above, this doesn't treat 0 as "empty" (skipping
// the range check) - only undefined/null/'' skip it, an actual 0 is still range-checked.
// Needs .nullable() explicitly: yup's .test() runs even on null values, so without
// it a conditionally hidden field (reset to null, e.g. via a FormField's
// displayField) fails validation before this test body's own null check ever runs.
export const optionalNumberBetweenValidator = (min: number, max: number) =>
  yup
    .string()
    .nullable()
    .test(
      'OPTIONAL_NUMBER_BETWEEN_VALIDATOR',
      t('validation.number_between', { min, max }),
      (val) => {
        if (val === undefined || val === null || val === '') {
          return true;
        }
        const number = Number(val);
        return number >= min && number <= max;
      },
    );

export const numberPositiveValidator = () =>
  yup
    .string()
    .test(
      'NUMBER_POSITIVE_VALIDATOR',
      t('validation.number_positive'),
      (val) => Number(val) >= 0,
    );

export const numberDivisibleByValidator = (divisibleBy: number) =>
  yup
    .string()
    .test(
      'NUMBER_DIVISIBLE_BY_VALIDATOR',
      t('validation.number_divisible_by', { divisibleBy }),
      (val) => Number(val) % divisibleBy === 0,
    );

export const dateInPastValidator = () =>
  yup
    .string()
    .test(
      'DATE_IN_PAST_VALIDATOR',
      t('validation.date_in_past'),
      (val) => new Date(val).getTime() < new Date().getTime(),
    );

export const dateInFutureValidator = () =>
  yup
    .string()
    .test(
      'DATE_IN_FUTURE_VALIDATOR',
      t('validation.date_in_future'),
      (val) => new Date(val).getTime() > new Date().getTime(),
    );

// TODO: remove parameter
export const emailValidator = () =>
  yup.string().email(t('validation.invalid-email-format'));

export const requireEmailValidator = () =>
  emailValidator().required(t('common.form.required_field'));
